import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  Plus, Search, Upload, MoreVertical, Download, RefreshCw, Ban, CarFront,
  AlertTriangle, Wallet, History, Loader2, FileText, X, CalendarClock,
} from 'lucide-react';

const RAMI = ['Auto', 'Moto', 'Casa', 'Vita', 'Infortuni', 'RC Professionale', 'Polizza Azienda', 'Salute', 'Viaggio', 'Altro'];
const FRAZIONAMENTI = [
  { value: 'annuale', label: 'Annuale' },
  { value: 'semestrale', label: 'Semestrale' },
  { value: 'mensile', label: 'Mensile' },
];

const STATO_BADGE = {
  attiva: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sostituita: 'bg-amber-100 text-amber-700 border-amber-200',
  annullata: 'bg-slate-200 text-slate-500 border-slate-300',
};

const emptyForm = {
  cliente_id: null, cliente_nome: '', cliente_cognome: '', cliente_telefono: '', cliente_email: '', cliente_codice_fiscale: '',
  compagnia: '', ramo: 'Auto', numero_polizza: '', targa: '', premio: '', premio_annuo: '', frazionamento: 'annuale',
  data_decorrenza: '', data_scadenza_polizza: '', data_scadenza_titolo: '', data_incasso: '', note: '', pdf_path: null, pdf_filename: null,
};

const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('it-IT'); } catch { return d; }
};

export const AgenziaPolizze = () => {
  const [searchParams] = useSearchParams();
  const clienteIdFilter = searchParams.get('cliente_id') || '';

  const [polizze, setPolizze] = useState([]);
  const [compagnie, setCompagnie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCompagnia, setFilterCompagnia] = useState('');
  const [filterStato, setFilterStato] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const [clienteQuery, setClienteQuery] = useState('');
  const [clienteResults, setClienteResults] = useState([]);
  const [clienteSelezionato, setClienteSelezionato] = useState(null);

  const [actionDialog, setActionDialog] = useState(null); // { type, polizza }
  const [actionForm, setActionForm] = useState({});
  const [actionSaving, setActionSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setA = (k, v) => setActionForm(f => ({ ...f, [k]: v }));

  const fetchPolizze = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterCompagnia) params.set('compagnia', filterCompagnia);
      if (filterStato) params.set('stato', filterStato);
      if (clienteIdFilter) params.set('cliente_id', clienteIdFilter);
      const res = await api.get(`/agenzia/polizze?${params.toString()}`);
      setPolizze(res.data || []);
    } catch {
      toast.error('Errore nel caricamento polizze');
    } finally {
      setLoading(false);
    }
  }, [search, filterCompagnia, filterStato, clienteIdFilter]);

  useEffect(() => { const t = setTimeout(fetchPolizze, 250); return () => clearTimeout(t); }, [fetchPolizze]);

  useEffect(() => {
    api.get('/agenzia/compagnie').then(res => setCompagnie(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clienteQuery || clienteQuery.length < 2) { setClienteResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/agenzia/clienti?search=${encodeURIComponent(clienteQuery)}`);
        setClienteResults(res.data || []);
      } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [clienteQuery]);

  const openNew = () => {
    setEditId(null);
    setForm({ ...emptyForm });
    setClienteSelezionato(null);
    setClienteQuery('');
    setFormOpen(true);
  };

  const openEdit = (p) => {
    setEditId(p.id);
    setForm({
      cliente_id: p.cliente_id, cliente_nome: p.cliente_nome || '', cliente_cognome: p.cliente_cognome || '',
      cliente_telefono: p.cliente_telefono || '', cliente_email: p.cliente_email || '', cliente_codice_fiscale: '',
      compagnia: p.compagnia || '', ramo: p.ramo || 'Auto', numero_polizza: p.numero_polizza || '',
      targa: p.targa || '', premio: p.premio ?? '', premio_annuo: p.premio_annuo ?? '', frazionamento: p.frazionamento || 'annuale',
      data_decorrenza: p.data_decorrenza || '', data_scadenza_polizza: p.data_scadenza_polizza || '',
      data_scadenza_titolo: p.data_scadenza_titolo || '', data_incasso: '', note: p.note || '',
      pdf_path: p.pdf_path || null, pdf_filename: p.pdf_filename || null,
    });
    setClienteSelezionato(p.cliente_id ? { id: p.cliente_id, nome: p.cliente_nome, cognome: p.cliente_cognome } : null);
    setClienteQuery('');
    setFormOpen(true);
  };

  const selezionaCliente = (c) => {
    setClienteSelezionato(c);
    setForm(f => ({
      ...f, cliente_id: c.id, cliente_nome: c.nome, cliente_cognome: c.cognome,
      cliente_telefono: c.telefono || '', cliente_email: c.email || '',
    }));
    setClienteQuery('');
    setClienteResults([]);
  };

  const rimuoviClienteSelezionato = () => {
    setClienteSelezionato(null);
    setForm(f => ({ ...f, cliente_id: null }));
  };

  const handleUploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/agenzia/polizze/estrai-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
      const ex = res.data.extracted || {};
      if (ex.error) {
        toast.error(ex.error);
      } else {
        toast.success('Dati estratti dal PDF: verifica e completa i campi mancanti');
      }
      setForm(f => ({
        ...f,
        cliente_nome: ex.nome_cliente || f.cliente_nome,
        cliente_cognome: ex.cognome_cliente || f.cliente_cognome,
        cliente_telefono: ex.telefono || f.cliente_telefono,
        cliente_email: ex.email || f.cliente_email,
        cliente_codice_fiscale: ex.codice_fiscale || f.cliente_codice_fiscale,
        compagnia: ex.compagnia || f.compagnia,
        ramo: RAMI.includes(ex.ramo) ? ex.ramo : f.ramo,
        numero_polizza: ex.numero_polizza || f.numero_polizza,
        targa: ex.targa || f.targa,
        premio: ex.premio ? String(ex.premio).replace(',', '.').replace(/[^0-9.]/g, '') : f.premio,
        premio_annuo: ex.premio_annuo ? String(ex.premio_annuo).replace(',', '.').replace(/[^0-9.]/g, '') : f.premio_annuo,
        frazionamento: FRAZIONAMENTI.some(fr => fr.value === ex.frazionamento) ? ex.frazionamento : f.frazionamento,
        data_decorrenza: ex.data_decorrenza || f.data_decorrenza,
        data_scadenza_polizza: ex.data_scadenza_polizza || f.data_scadenza_polizza,
        data_scadenza_titolo: ex.data_scadenza_titolo || f.data_scadenza_titolo,
        data_incasso: ex.data_incasso || f.data_incasso,
        pdf_path: res.data.pdf_path || f.pdf_path,
        pdf_filename: res.data.pdf_filename || f.pdf_filename,
      }));
      setClienteSelezionato(null);
    } catch (err) {
      toast.error('Errore nella lettura del PDF');
    } finally {
      setExtracting(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.cliente_nome.trim() || !form.cliente_cognome.trim()) {
      toast.error('Nome e cognome cliente obbligatori (se non selezionato dalla lista, verranno usati per creare un nuovo cliente)');
      return;
    }
    if (!form.compagnia) { toast.error('Seleziona la compagnia'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        premio: form.premio ? parseFloat(form.premio) : null,
        premio_annuo: form.premio_annuo ? parseFloat(form.premio_annuo) : null,
        data_incasso: form.data_incasso || null,
      };
      if (editId) {
        await api.patch(`/agenzia/polizze/${editId}`, payload);
        toast.success('Polizza aggiornata');
      } else {
        await api.post('/agenzia/polizze', payload);
        toast.success('Polizza salvata');
      }
      setFormOpen(false);
      fetchPolizze();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const scaricaPdf = async (p) => {
    try {
      const res = await api.get(`/agenzia/polizze/${p.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = p.pdf_filename || 'polizza.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Nessun PDF disponibile per questa polizza');
    }
  };

  const openAction = (type, polizza) => {
    setActionDialog({ type, polizza });
    if (type === 'incassa') setActionForm({ data_incasso: new Date().toISOString().split('T')[0], rinnova_scadenza: true });
    else if (type === 'cambio_targa') setActionForm({ nuova_targa: '', data: new Date().toISOString().split('T')[0] });
    else if (type === 'sostituisci') setActionForm({ numero_polizza: '', compagnia: polizza.compagnia, premio: polizza.premio || '', frazionamento: polizza.frazionamento, data_decorrenza: '', data_scadenza_polizza: '', data_scadenza_titolo: '', motivo: '' });
    else if (type === 'annulla') setActionForm({ motivo: '' });
    else if (type === 'sinistro') setActionForm({ data_sinistro: new Date().toISOString().split('T')[0], dinamica: '', controparte: '' });
    else if (type === 'rinnova') setActionForm({ data_scadenza_polizza: '', premio_annuo: polizza.premio_annuo || '', note: '' });
  };

  const closeAction = () => { setActionDialog(null); setActionForm({}); };

  const submitAction = async () => {
    if (!actionDialog) return;
    const { type, polizza } = actionDialog;
    setActionSaving(true);
    try {
      if (type === 'incassa') {
        await api.post(`/agenzia/polizze/${polizza.id}/incassa`, actionForm);
        toast.success('Incasso registrato');
      } else if (type === 'cambio_targa') {
        if (!actionForm.nuova_targa) { toast.error('Inserisci la nuova targa'); setActionSaving(false); return; }
        await api.post(`/agenzia/polizze/${polizza.id}/cambio-targa`, actionForm);
        toast.success('Targa aggiornata');
      } else if (type === 'sostituisci') {
        await api.post(`/agenzia/polizze/${polizza.id}/sostituisci`, { ...actionForm, premio: actionForm.premio ? parseFloat(actionForm.premio) : null });
        toast.success('Polizza sostituita: creata la nuova polizza');
      } else if (type === 'annulla') {
        await api.post(`/agenzia/polizze/${polizza.id}/annulla`, actionForm);
        toast.success('Polizza annullata');
      } else if (type === 'sinistro') {
        await api.post(`/agenzia/polizze/${polizza.id}/denuncia-sinistro`, actionForm);
        toast.success('Sinistro segnalato nel modulo Sinistri');
      } else if (type === 'rinnova') {
        await api.post(`/agenzia/polizze/${polizza.id}/rinnova`, {
          ...actionForm,
          premio_annuo: actionForm.premio_annuo ? parseFloat(actionForm.premio_annuo) : null,
          data_scadenza_polizza: actionForm.data_scadenza_polizza || null,
        });
        toast.success('Contratto rinnovato');
      }
      closeAction();
      fetchPolizze();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nell\'operazione');
    } finally {
      setActionSaving(false);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Polizze</h1>
          <p className="text-sm text-slate-500">Portafoglio polizze - CRM Agenzia</p>
        </div>
        <Button onClick={openNew} data-testid="agenzia-nuova-polizza">
          <Plus size={16} className="mr-2" /> Nuova Polizza
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative w-64">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Cerca cliente, numero, targa..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={filterCompagnia || 'tutte'} onValueChange={(v) => setFilterCompagnia(v === 'tutte' ? '' : v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Compagnia" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le compagnie</SelectItem>
            {compagnie.map(c => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStato || 'tutti'} onValueChange={(v) => setFilterStato(v === 'tutti' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti gli stati</SelectItem>
            <SelectItem value="attiva">Attiva</SelectItem>
            <SelectItem value="sostituita">Sostituita</SelectItem>
            <SelectItem value="annullata">Annullata</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>Compagnia</TableHead>
              <TableHead>Ramo / Targa</TableHead>
              <TableHead>N. Polizza</TableHead>
              <TableHead>Premio rata</TableHead>
              <TableHead>Scad. Titolo</TableHead>
              <TableHead>Scad. Polizza</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Caricamento...</TableCell></TableRow>
            ) : polizze.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Nessuna polizza trovata</TableCell></TableRow>
            ) : polizze.map((p) => (
              <TableRow key={p.id} data-testid={`agenzia-polizza-row-${p.id}`}>
                <TableCell className="font-medium text-slate-900">{p.cliente_nome} {p.cliente_cognome}</TableCell>
                <TableCell>{p.compagnia}</TableCell>
                <TableCell>{p.ramo}{p.targa ? ` - ${p.targa}` : ''}</TableCell>
                <TableCell>{p.numero_polizza || '-'}</TableCell>
                <TableCell>{p.premio ? `€ ${Number(p.premio).toFixed(2)}` : '-'} <span className="text-slate-400 text-xs">({p.frazionamento})</span></TableCell>
                <TableCell>{fmtDate(p.data_scadenza_titolo)}</TableCell>
                <TableCell className="text-slate-500">{fmtDate(p.data_scadenza_polizza)}</TableCell>
                <TableCell><Badge className={STATO_BADGE[p.stato] || ''} variant="outline">{p.stato}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon"><MoreVertical size={16} /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEdit(p)}>Modifica dati</DropdownMenuItem>
                      {p.pdf_path && <DropdownMenuItem onClick={() => scaricaPdf(p)}><Download size={14} className="mr-2" />Scarica PDF</DropdownMenuItem>}
                      {p.stato === 'attiva' && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openAction('incassa', p)}><Wallet size={14} className="mr-2" />Segna come incassata</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAction('cambio_targa', p)}><CarFront size={14} className="mr-2" />Cambio targa</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAction('sostituisci', p)}><RefreshCw size={14} className="mr-2" />Sostituisci polizza</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAction('rinnova', p)}><CalendarClock size={14} className="mr-2" />Rinnova contratto</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openAction('sinistro', p)}><AlertTriangle size={14} className="mr-2" />Denuncia sinistro</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => openAction('annulla', p)} className="text-red-600"><Ban size={14} className="mr-2" />Annulla polizza</DropdownMenuItem>
                      </>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Dialog Crea/Modifica Polizza */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifica Polizza' : 'Nuova Polizza'}</DialogTitle>
          </DialogHeader>

          {!editId && (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
              <label className="cursor-pointer flex flex-col items-center gap-2 text-slate-500">
                {extracting ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} />}
                <span className="text-sm">{extracting ? 'Lettura PDF in corso...' : 'Carica il PDF della polizza per compilare automaticamente i campi'}</span>
                <input type="file" accept=".pdf,image/*" className="hidden" onChange={handleUploadPdf} disabled={extracting} />
              </label>
              {form.pdf_filename && <p className="text-xs text-emerald-600 mt-2 flex items-center justify-center gap-1"><FileText size={12} />{form.pdf_filename}</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label>Cliente</Label>
            {clienteSelezionato ? (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <span className="text-sm font-medium">{clienteSelezionato.nome} {clienteSelezionato.cognome}</span>
                <Button variant="ghost" size="icon" onClick={rimuoviClienteSelezionato}><X size={14} /></Button>
              </div>
            ) : (
              <div className="relative">
                <Input placeholder="Cerca cliente esistente per nome/cognome/CF..." value={clienteQuery} onChange={(e) => setClienteQuery(e.target.value)} />
                {clienteResults.length > 0 && (
                  <div className="absolute z-10 w-full bg-white border border-slate-200 rounded-md shadow-lg mt-1 max-h-48 overflow-y-auto">
                    {clienteResults.map(c => (
                      <button key={c.id} onClick={() => selezionaCliente(c)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b last:border-0">
                        {c.nome} {c.cognome} {c.telefono ? `- ${c.telefono}` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <p className="text-xs text-slate-400">Se il cliente non esiste, compila i campi sotto: verra' creato automaticamente al salvataggio.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.cliente_nome} onChange={(e) => set('cliente_nome', e.target.value)} disabled={!!clienteSelezionato} />
            </div>
            <div>
              <Label>Cognome *</Label>
              <Input value={form.cliente_cognome} onChange={(e) => set('cliente_cognome', e.target.value)} disabled={!!clienteSelezionato} />
            </div>
            <div>
              <Label>Telefono {!form.cliente_telefono && <span className="text-amber-600">(mancante)</span>}</Label>
              <Input value={form.cliente_telefono} onChange={(e) => set('cliente_telefono', e.target.value)} />
            </div>
            <div>
              <Label>Email {!form.cliente_email && <span className="text-amber-600">(mancante)</span>}</Label>
              <Input value={form.cliente_email} onChange={(e) => set('cliente_email', e.target.value)} />
            </div>

            <div>
              <Label>Compagnia *</Label>
              <Select value={form.compagnia || undefined} onValueChange={(v) => set('compagnia', v)}>
                <SelectTrigger><SelectValue placeholder="Seleziona compagnia" /></SelectTrigger>
                <SelectContent>
                  {compagnie.map(c => <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ramo</Label>
              <Select value={form.ramo} onValueChange={(v) => set('ramo', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RAMI.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Numero Polizza</Label>
              <Input value={form.numero_polizza} onChange={(e) => set('numero_polizza', e.target.value)} />
            </div>
            <div>
              <Label>Targa (se Auto/Moto)</Label>
              <Input value={form.targa} onChange={(e) => set('targa', e.target.value.toUpperCase())} />
            </div>
            <div>
              <Label>Premio rata (€) <span className="text-slate-400 font-normal">- importo dovuto ad ogni scadenza</span></Label>
              <Input type="number" step="0.01" value={form.premio} onChange={(e) => set('premio', e.target.value)} />
            </div>
            <div>
              <Label>Premio annuo (€) <span className="text-slate-400 font-normal">- opzionale, solo se diverso dalla rata</span></Label>
              <Input type="number" step="0.01" value={form.premio_annuo} onChange={(e) => set('premio_annuo', e.target.value)} />
            </div>
            <div>
              <Label>Frazionamento</Label>
              <Select value={form.frazionamento} onValueChange={(v) => set('frazionamento', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FRAZIONAMENTI.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data Decorrenza</Label>
              <Input type="date" value={form.data_decorrenza || ''} onChange={(e) => set('data_decorrenza', e.target.value)} />
            </div>
            <div>
              <Label>Scadenza Titolo <span className="text-slate-400 font-normal">- prossima rata da pagare</span></Label>
              <Input type="date" value={form.data_scadenza_titolo || ''} onChange={(e) => set('data_scadenza_titolo', e.target.value)} />
            </div>
            <div>
              <Label>Scadenza Polizza <span className="text-slate-400 font-normal">- fine contratto</span></Label>
              <Input type="date" value={form.data_scadenza_polizza || ''} onChange={(e) => set('data_scadenza_polizza', e.target.value)} />
            </div>
            <div>
              <Label>Data Incasso <span className="text-slate-400 font-normal">- se gia' pagata</span></Label>
              <Input type="date" value={form.data_incasso || ''} onChange={(e) => set('data_incasso', e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => set('note', e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setFormOpen(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvataggio...' : 'Salva Polizza'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog azioni lifecycle */}
      <Dialog open={!!actionDialog} onOpenChange={(v) => !v && closeAction()}>
        <DialogContent className="max-w-md">
          {actionDialog?.type === 'incassa' && (
            <>
              <DialogHeader><DialogTitle>Registra Incasso</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Data incasso</Label><Input type="date" value={actionForm.data_incasso} onChange={(e) => setA('data_incasso', e.target.value)} /></div>
                <div className="flex items-center gap-2">
                  <Checkbox checked={actionForm.rinnova_scadenza} onCheckedChange={(v) => setA('rinnova_scadenza', !!v)} id="rinnova" />
                  <Label htmlFor="rinnova">Calcola automaticamente la prossima scadenza</Label>
                </div>
              </div>
            </>
          )}
          {actionDialog?.type === 'cambio_targa' && (
            <>
              <DialogHeader><DialogTitle>Cambio Targa</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nuova targa</Label><Input value={actionForm.nuova_targa} onChange={(e) => setA('nuova_targa', e.target.value.toUpperCase())} /></div>
                <div><Label>Data cambio</Label><Input type="date" value={actionForm.data} onChange={(e) => setA('data', e.target.value)} /></div>
              </div>
            </>
          )}
          {actionDialog?.type === 'sostituisci' && (
            <>
              <DialogHeader><DialogTitle>Sostituisci Polizza</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Verra' creata una nuova polizza collegata; quella attuale sara' marcata come sostituita.</p>
                <div><Label>Compagnia</Label><Input value={actionForm.compagnia} onChange={(e) => setA('compagnia', e.target.value)} /></div>
                <div><Label>Nuovo numero polizza</Label><Input value={actionForm.numero_polizza} onChange={(e) => setA('numero_polizza', e.target.value)} /></div>
                <div><Label>Premio (€)</Label><Input type="number" step="0.01" value={actionForm.premio} onChange={(e) => setA('premio', e.target.value)} /></div>
                <div><Label>Data decorrenza</Label><Input type="date" value={actionForm.data_decorrenza} onChange={(e) => setA('data_decorrenza', e.target.value)} /></div>
                <div><Label>Scadenza titolo (prossima rata)</Label><Input type="date" value={actionForm.data_scadenza_titolo} onChange={(e) => setA('data_scadenza_titolo', e.target.value)} /></div>
                <div><Label>Scadenza polizza (fine contratto)</Label><Input type="date" value={actionForm.data_scadenza_polizza} onChange={(e) => setA('data_scadenza_polizza', e.target.value)} /></div>
                <div><Label>Motivo</Label><Textarea rows={2} value={actionForm.motivo} onChange={(e) => setA('motivo', e.target.value)} /></div>
              </div>
            </>
          )}
          {actionDialog?.type === 'rinnova' && (
            <>
              <DialogHeader><DialogTitle>Rinnova Contratto</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-slate-500">Aggiorna solo la scadenza del contratto (rinnovo annuale); la scadenza rata continua ad avanzare separatamente con gli incassi.</p>
                <div><Label>Nuova scadenza polizza</Label><Input type="date" value={actionForm.data_scadenza_polizza} onChange={(e) => setA('data_scadenza_polizza', e.target.value)} /></div>
                <div><Label>Nuovo premio annuo (€)</Label><Input type="number" step="0.01" value={actionForm.premio_annuo} onChange={(e) => setA('premio_annuo', e.target.value)} /></div>
                <div><Label>Note</Label><Textarea rows={2} value={actionForm.note} onChange={(e) => setA('note', e.target.value)} /></div>
              </div>
            </>
          )}
          {actionDialog?.type === 'annulla' && (
            <>
              <DialogHeader><DialogTitle>Annulla Polizza</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Motivo annullamento</Label><Textarea rows={3} value={actionForm.motivo} onChange={(e) => setA('motivo', e.target.value)} /></div>
              </div>
            </>
          )}
          {actionDialog?.type === 'sinistro' && (
            <>
              <DialogHeader><DialogTitle>Denuncia Sinistro</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-slate-500 flex items-center gap-1"><History size={12} />Verra' creata una segnalazione nel modulo Sinistri gia' precompilata.</p>
                <div><Label>Data sinistro</Label><Input type="date" value={actionForm.data_sinistro} onChange={(e) => setA('data_sinistro', e.target.value)} /></div>
                <div><Label>Controparte</Label><Input value={actionForm.controparte} onChange={(e) => setA('controparte', e.target.value)} /></div>
                <div><Label>Dinamica</Label><Textarea rows={3} value={actionForm.dinamica} onChange={(e) => setA('dinamica', e.target.value)} /></div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeAction}>Chiudi</Button>
            <Button onClick={submitAction} disabled={actionSaving}>{actionSaving ? 'Attendere...' : 'Conferma'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
